/**
 * The coach's saved formations: loaded once, then kept in step with the store
 * as they save, recall and delete.
 */

import { useCallback, useEffect, useState } from 'react';
import type { SavedFormation } from '../core/index.ts';
import type { FormationStore } from '../adapters/index.ts';
import type { BoardApi } from './useBoard.ts';

export interface FormationsApi {
  readonly saved: readonly SavedFormation[];
  /** Stored but unreadable here — saved by a newer version. Kept on the server, not shown. */
  readonly unreadable: number;
  readonly error: string | null;
  /** The saved formation already using this name, which saving under it would replace. */
  named(name: string): SavedFormation | undefined;
  /** Saves the active phase under `name`, replacing a saved formation of the same name. */
  save(name: string): Promise<boolean>;
  recall(id: string): void;
  remove(id: string): Promise<void>;
}

const sameName = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

export function useFormations(store: FormationStore, api: BoardApi): FormationsApi {
  const [saved, setSaved] = useState<readonly SavedFormation[]>([]);
  const [unreadable, setUnreadable] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    store
      .list()
      .then((list) => {
        if (cancelled) return;
        setSaved(list.formations);
        setUnreadable(list.unreadable);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  const named = useCallback((name: string) => saved.find((f) => sameName(f.name, name)), [saved]);

  const save = useCallback(
    async (name: string) => {
      if (!name.trim()) return false;
      const existing = named(name);
      const formation = api.captureFormation(existing?.id ?? api.newId('fm'), name, existing?.createdAt);
      try {
        await store.save(formation);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
        return false;
      }
      setError(null);
      setSaved((current) =>
        existing ? current.map((f) => (f.id === existing.id ? formation : f)) : [...current, formation],
      );
      return true;
    },
    [api, named, store],
  );

  const recall = useCallback(
    (id: string) => {
      const formation = saved.find((f) => f.id === id);
      if (formation) api.recallFormation(formation);
    },
    [api, saved],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await store.remove(id);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
        return;
      }
      setError(null);
      setSaved((current) => current.filter((f) => f.id !== id));
    },
    [store],
  );

  return { saved, unreadable, error, named, save, recall, remove };
}
